import { useEffect, useRef } from 'react'
import { rootStore } from 'stores'
import { firebaseMessagingService } from 'services/firebaseMessaging.service'

export interface FcmSwMessage {
  __from: 'fcm-sw'
  type: string
  data?: any
  timestamp: number
  targetUrl?: string
  notificationData?: any
}

export function useFcmSwBridge() {
  const { messageStore, notificationStore } = rootStore
  const isListeningRef = useRef(false)

  useEffect(() => {
    
    if ('serviceWorker' in navigator && !isListeningRef.current) {
      
      // Check current Service Worker registrations
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => {
          console.log('SW Registration:', {
            scope: reg.scope,
            active: reg.active?.state,
            scriptURL: reg.active?.scriptURL
          })
        })
      })
      
      const handleMessage = (event: MessageEvent) => {
        const msg = event.data as FcmSwMessage
        
        
        // Only process messages from FCM Service Worker
        if (!msg || msg.__from !== 'fcm-sw') {
          return
        }

        switch (msg.type) {
          case 'message':
          case 'support_message':
            handleMessageUpdate(msg)
            break
          case 'notification':
            handleNotificationUpdate(msg)
            break
          case 'workplan_lock_forced': {
            break
          }
          case 'SW_ACTIVATED':
            console.log('FCM Service Worker activated')
            break
          default:
            console.log('Unknown FCM SW message type:', msg.type)
        }
      }

      const handleMessageUpdate = (msg: FcmSwMessage) => {
        const { data } = msg
        if (!data) {
          return
        }

        console.log('Handling message update from FCM SW:', data)

        const currentPath = window.location.pathname
        const isInMessagesPage = currentPath.includes('/messages')

        if (data.type === 'message') {
          // Refresh general messages
          messageStore.fetchGeneralMessageThreads(20, messageStore.generaltMessageKeyword, false)
          messageStore.getUnreadThread()

          // If we're currently viewing the thread that received a new message, refresh messages
          if (data.threadId === messageStore.currentGeneralThreadId && isInMessagesPage) {
            messageStore.getMessages()
          }
        } else if (data.type === 'support_message') {
          // Refresh support messages
          messageStore.getSupportMessageThreads(20, false)
          messageStore.getUnreadThread()

          // If we're currently viewing the thread that received a new message, refresh messages
          if (data.threadId === messageStore.currentSupportThreadId && isInMessagesPage) {
            messageStore.getSupportMessages()
            messageStore.getThreadDetail()
          }
        }
      }

      const handleNotificationUpdate = (msg: FcmSwMessage) => {
        const { data } = msg
        if (!data) {
          return
        }

        console.log('Handling notification update from FCM SW:', data)

        const currentUserId = rootStore.authStore.userDetail?.id

        // Only refresh if the notification is for the current user
        if (data.userId === currentUserId || !data.userId) {
          notificationStore.fetchNotifications()
          notificationStore.aggregateCountNotifications()
        }
      }

      const handleNotificationClick = (msg: FcmSwMessage) => {
        const { targetUrl, notificationData } = msg
        if (!targetUrl) {
          return
        }

        console.log('Handling notification click from FCM SW:', { targetUrl, notificationData })

        // Navigate to target URL
        if (targetUrl && targetUrl !== window.location.pathname + window.location.search) {
          window.location.href = targetUrl
        }
      }

      // Listen for messages from Service Worker
      navigator.serviceWorker.addEventListener('message', handleMessage)
      isListeningRef.current = true


      // Cleanup function
      const cleanup = () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage)
        isListeningRef.current = false
      }

      return cleanup
    }
    
    // Return empty cleanup function when no Service Worker
    return () => {}
  }, [messageStore, notificationStore])
} 